import { useRef, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@ideon-api/ideon-ui'
import { sdkErrorMessage } from '../../lib/sdk-client'
import { useRevokeMachineUser, type MachineUser } from '../../lib/use-service-accounts'

interface RevokeMachineUserDialogProps {
  isOpen: boolean
  onClose: () => void
  slug: string
  machineUser: MachineUser
  onRevoked: () => void
}

/**
 * Destroying a machine user deletes the local row AND the real Auth0 application.
 * There is no soft delete and no undo, and anything still using the key stops
 * working the moment this succeeds — so confirmation is typing the key's name,
 * not clicking through a prompt. Composed from Dialog primitives rather than a
 * shared component: this is currently the only surface that needs it.
 *
 * The caller mounts this only with a subject, so every open is a fresh mount and
 * needs no reset. An unconditional mount would re-render with a null subject
 * during Radix's 200ms exit animation, briefly reading "Revoke" over an empty
 * confirm field.
 */
export function RevokeMachineUserDialog({
  isOpen,
  onClose,
  slug,
  machineUser,
  onRevoked,
}: RevokeMachineUserDialogProps) {
  const revokeMachineUser = useRevokeMachineUser(slug)
  const [typedName, setTypedName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submittingRef = useRef(false)

  const confirmed = typedName.trim() === machineUser.name

  const handleOpenChange = (open: boolean) => {
    if (open || submitting) return
    onClose()
  }

  const handleRevoke = async () => {
    if (submittingRef.current || !confirmed) return
    submittingRef.current = true
    setSubmitting(true)
    setError(null)
    try {
      await revokeMachineUser(machineUser.id)
      onRevoked()
      onClose()
    } catch (e: unknown) {
      setError(sdkErrorMessage(e, 'Could not revoke the machine user.'))
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke {machineUser.name}</DialogTitle>
        </DialogHeader>

        <Alert variant="warning">
          This deletes the Auth0 application behind this key. Anything still using it stops
          working immediately, and the key cannot be restored.
        </Alert>

        {error && <Alert variant="error">{error}</Alert>}

        <FormField label={`Type ${machineUser.name} to confirm`} htmlFor="revoke-confirm">
          <Input
            id="revoke-confirm"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            disabled={submitting}
          />
        </FormField>

        <DialogFooter>
          <Button variant="text" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRevoke} disabled={!confirmed || submitting}>
            {submitting ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
